import React, { useState, useEffect } from "react";
import ENDPOINTS from '../config/apiEndPoints';
import LoadingSpinner from "./LoadingSpinner";
import styles from "./PartyWiseResults.module.css"
import {shareComponent, ShareModal} from "../config/shareUtils";

const LOCAL_BODY_TYPES = [
  { value: 'corp', label: 'Corporation' },
  { value: 'jila', label: 'Dist. Panchayat' },
  { value: 'muncipality', label: 'Municipality' },
  { value: 'blockpanchayat', label: 'Block Panchayat' },
  { value: 'gramapanchayat', label: 'Grama Panchayat' }
];

const PartyWiseResults = ({ list }) => {
  // State management
  const [localBodyType, setLocalBodyType] = useState('corp');
  const [isLocalBodyDropdownOpen, setIsLocalBodyDropdownOpen] = useState(false);
  const [localBodyContent, setLocalBodyContent] = useState([]);
  const [selectedContent, setSelectedContent] = useState('');
  const [electionData, setElectionData] = useState(null);
  const [isLoading, setLoading] = useState(false);
// eslint-disable-next-line no-unused-vars
  const [districts, setDistricts] = useState([]);
  const [selectedDistrict, setSelectedDistrict] = useState('');
  const [localBodies, setLocalBodies] = useState([]);
  const [selectedLocalBody, setSelectedLocalBody] = useState('');
  const [shareState, setShareState] = useState(null);

  const handleShare = async () => {
    const shareUrl = list
      ? `${window.location.origin}${window.location.pathname}?show=parties&list=${encodeURIComponent(list)}`
      : `${window.location.origin}${window.location.pathname}?show=parties`;

    const result = await shareComponent('Statewide Results', shareUrl);
    setShareState(result);
  };

  // Fetch content when localBodyType changes
  useEffect(() => {
    const fetchContent = async () => {
      if (!localBodyType) return;

      try {
        let endpoint;
        switch (localBodyType) {
          case 'corp':
            endpoint = ENDPOINTS.CORP_TEXT;
            break;
          case 'jila':
            endpoint = ENDPOINTS.JILA_TEXT;
            break;
          default:
            endpoint = ENDPOINTS.DISTRICTS_TEXT;
        }

        const response = await fetch(endpoint, {
          headers: {
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache'
          }
        });

        if (!response.ok) throw new Error('Failed to load content');

        const text = await response.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(text, 'text/html');
        const options = Array.from(doc.querySelectorAll('option'));

        const contentItems = options
          .filter(option => {
            const value = option.value?.trim().toLowerCase() || '';
            return value && !value.includes('select');
          })
          .map(option => ({
            value: option.value,
            label: option.textContent
            // label: option.textContent.toLowerCase() === 'trivandrum' ? 'Thiruvananthapuram' : option.textContent
          }));

        setLocalBodyContent(contentItems);
        // Set the first item as selected if available
        if (contentItems.length > 0) {
          setSelectedContent(contentItems[0].value);
        }
      } catch (error) {
        console.error('Error loading content:', error);
        setLocalBodyContent([]);
        setSelectedContent('');
      }
    };

    fetchContent().then(() => {
      setLoading(false);
    });
  }, [localBodyType]);

  // Event handlers
  const handleLocalBodySelect = (localBody, e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    setLocalBodyType(localBody);
    setIsLocalBodyDropdownOpen(false);
  };

  const handleContentSelect = (content) => {
    setLoading(true);
    const formattedDistrict = content.charAt(0).toUpperCase() + content.slice(1).toLowerCase();

    if (list) {
      window.location.href = `${window.location.pathname}?show=parties&list=${encodeURIComponent(formattedDistrict)}`;
    } else {
      setSelectedContent(formattedDistrict);
    }
  };

  useEffect(() => {
    if (list && list !== 'state') {
      const formattedList = list.charAt(0).toUpperCase() + list.slice(1).toLowerCase();
      const matchedDistrict = districts.find(
        d => d.value.toLowerCase() === formattedList.toLowerCase()
      );
      if (matchedDistrict) {
        setSelectedContent(matchedDistrict.value);
      }
    }
  }, [list, districts]);

  useEffect(() => {
    const fetchElectionData = async () => {
      // Don't fetch if we don't have a valid ID
      if ((!selectedContent && !selectedLocalBody) ||
        (['muncipality', 'blockpanchayat', 'gramapanchayat'].includes(localBodyType) && !selectedLocalBody) ||
        (['corp', 'jila'].includes(localBodyType) && !selectedContent)) {
        return;
      }

      setLoading(true);

      try {
        const now = new Date();
        const timestamp = `${String(now.getHours()).padStart(2, '0')}${String(now.getMinutes()).padStart(2, '0')}`;

        const id = list && list !== 'state'
          ? list
          : (['muncipality', 'blockpanchayat', 'gramapanchayat'].includes(localBodyType)
            ? selectedLocalBody
            : selectedContent);

        const endpointTemplate = list === 'state'
          ? ENDPOINTS.PARTYWISE_STATE_2025
          : list
            ? ENDPOINTS.PARTYWISE_2025(list)
            : ENDPOINTS.ELECTION_DATA_2025(id);

        const response = await fetch(`${endpointTemplate}?ver=${timestamp}`, {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache',
            'Expires': '0'
          }
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch data: ${response.statusText}`);
        }

        const data = await response.json();
        setElectionData(data);
      } catch (err) {
        console.error('Error fetching election data:', err);
        setElectionData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchElectionData();
  }, [selectedContent, selectedLocalBody, localBodyType, list]);

  // Derived values
  const localBodyTypeLabel = LOCAL_BODY_TYPES.find(
    lb => lb.value === localBodyType
  )?.label || 'Select Local Body';

  const fetchLocalBodies = async (localBodyType, district) => {
    try {
      let endpoint;
      switch (localBodyType) {
        case 'muncipality':
          endpoint = ENDPOINTS.MUNICIPALITY_TEXT(district);
          break;
        case 'blockpanchayat':
          endpoint = ENDPOINTS.BLOCK_PANCHAYATH_TEXT(district);
          break;
        case 'gramapanchayat':
          endpoint = ENDPOINTS.GRAMA_PANCHAYATH_TEXT(district);
          break;
        default:
          return [];
      }

      const response = await fetch(endpoint, {
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        }
      });

      if (!response.ok) throw new Error('Failed to load local bodies');

      const text = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(text, 'text/html');
      const options = Array.from(doc.querySelectorAll('option'));

      return options
        .filter(option => {
          const value = option.value?.trim().toLowerCase() || '';
          return value && !value.includes('select');
        })
        .map(option => ({
          value: option.value,
          label: option.textContent
        }));
    } catch (error) {
      console.error('Error loading local bodies:', error);
      return [];
    }
  };

  useEffect(() => {
    const fetchDistricts = async () => {
      try {
        const response = await fetch(ENDPOINTS.DISTRICTS_TEXT, {
          headers: {
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache'
          }
        });

        if (!response.ok) throw new Error('Failed to load districts');

        const text = await response.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(text, 'text/html');
        const options = Array.from(doc.querySelectorAll('option'));

        const districtItems = options
          .filter(option => option.value && option.value.trim().toLowerCase() !== 'select district')
          .map(option => ({
            value: option.value,
            label: option.textContent
          }));

        setDistricts(districtItems);
        if (districtItems.length > 0) {
          setSelectedDistrict(districtItems[0].value);
        }
      } catch (error) {
        console.error('Error loading districts:', error);
        setDistricts([]);
      }
    };

    fetchDistricts();
  }, []);

// Fetch local bodies when district or localBodyType changes
  useEffect(() => {
    const loadLocalBodies = async () => {
      if (!selectedDistrict) return;

      const bodies = await fetchLocalBodies(localBodyType, selectedDistrict);
      setLocalBodies(bodies);
      setSelectedLocalBody(bodies.length > 0 ? bodies[0].value : '');
    };

    if (['muncipality', 'blockpanchayat', 'gramapanchayat'].includes(localBodyType) && selectedDistrict) {
      loadLocalBodies();
    } else {
      setLocalBodies([]);
      setSelectedLocalBody('');
    }
  }, [localBodyType, selectedDistrict]);

  return (
    <div className={`local-body-container ${styles.partiesContainer}`}>
      <div className="allianceHeader" style={{position: 'relative'}}>
        ALLIANCE / PARTY WISE
        <svg
          className="share-icon"
          xmlns="http://www.w3.org/2000/svg"
          width="18"
          height="18"
          viewBox="0 0 18 18"
          fill="none"
          onClick={handleShare}
          style={{ cursor: 'pointer', position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)' }}
          role="button"
          aria-label="Share Party-wise Results"
        >
          <g clipPath="url(#clip0_644_1392)">
            <path fillRule="evenodd" clipRule="evenodd" d="M14.7371 0.599674C13.8626 -0.547357 12.007 0.0610694 12.007 1.49485V2.93454C9.17783 2.90105 7.24249 3.87355 5.95335 5.23165C4.57526 6.68347 4.00844 8.49855 3.77091 9.73965C3.65216 10.3601 4.05917 10.8103 4.48693 10.9658C4.89557 11.1143 5.44162 11.0507 5.80602 10.6342C6.84027 9.45202 8.84963 7.8021 12.007 7.9098V9.75517C12.007 11.1889 13.8626 11.7973 14.7371 10.6503L17.545 6.96775C18.1517 6.17205 18.1517 5.07795 17.545 4.28224L14.7371 0.599674ZM5.61151 8.70712C6.96541 7.52557 8.90888 6.41676 12.007 6.41676H12.7655C13.1844 6.41676 13.5239 6.75077 13.5239 7.16278L13.5238 9.75517L16.3316 6.07258C16.5338 5.80735 16.5338 5.44265 16.3316 5.17741L13.5238 1.49485V3.68053C13.5238 4.09252 13.1843 4.42651 12.7654 4.42651H12.007C8.59448 4.42651 7.06197 6.24985 7.06197 6.24985C6.35643 6.99313 5.90238 7.8765 5.61151 8.70712Z" fill="#403E3E"/>
            <path d="M5.25 0.75293H3C1.75736 0.75293 0.75 1.76028 0.75 3.00293V15.0029C0.75 16.2456 1.75736 17.2529 3 17.2529H15C16.2427 17.2529 17.25 16.2456 17.25 15.0029V12.7529C17.25 12.3387 16.9142 12.0029 16.5 12.0029C16.0858 12.0029 15.75 12.3387 15.75 12.7529V15.0029C15.75 15.4171 15.4142 15.7529 15 15.7529H3C2.58579 15.7529 2.25 15.4171 2.25 15.0029V3.00293C2.25 2.58871 2.58579 2.25293 3 2.25293H5.25C5.66421 2.25293 6 1.91714 6 1.50293C6 1.08871 5.66421 0.75293 5.25 0.75293Z" fill="#403E3E"/>
          </g>
          <defs>
            <clipPath id="clip0_644_1392">
              <rect width="18" height="18" fill="white"/>
            </clipPath>
          </defs>
        </svg>
      </div>

      {!list && (
        <>
          <div className={`local-body-selector ${styles.localBodySelector2}`}>
            {/* Local Body Type Dropdown */}
            <div
              className={`dropdown dropdown-2 ${styles.dropdown}`}
              onBlur={(e) => {
                if (!e.currentTarget.contains(e.relatedTarget)) {
                  setIsLocalBodyDropdownOpen(false);
                }
              }}
            >
              <button
                className="dropdown-toggle"
                onClick={() => setIsLocalBodyDropdownOpen(!isLocalBodyDropdownOpen)}
                aria-haspopup="listbox"
                aria-expanded={isLocalBodyDropdownOpen}
                aria-label="Select local body type"
              >
                {localBodyTypeLabel}
                <svg width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                  <path d="M1 1.5L6 6.5L11 1.5" stroke="#403E3E" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>

              {isLocalBodyDropdownOpen && (
                <div
                  className="dropdown-menu"
                  role="listbox"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={(e) => e.stopPropagation()}
                >
                  {LOCAL_BODY_TYPES.map((localBody) => (
                    <div
                      key={localBody.value}
                      role="option"
                      aria-selected={localBodyType === localBody.value}
                      className={`dropdown-item ${localBodyType === localBody.value ? 'active' : ''}`}
                      onClick={(e) => handleLocalBodySelect(localBody.value, e)}
                    >
                      {localBody.label}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className={`select-wrapper select-wrapper-1 ${styles.selectWrapper}`}>
              <select
                name="districts"
                className="form-select"
                value={['corp', 'jila'].includes(localBodyType) ? selectedContent : selectedDistrict}
                onChange={(e) => {
                  if (['corp', 'jila'].includes(localBodyType)) {
                    handleContentSelect(e.target.value);
                  } else {
                    setSelectedDistrict(e.target.value);
                  }
                }}
                disabled={['corp', 'jila'].includes(localBodyType) ? localBodyContent.length === 0 : districts.length === 0}
              >
                {['corp', 'jila'].includes(localBodyType) ? (
                  localBodyContent.map((item) => (
                    <option key={item.value} value={item.value}>
                      {item.label}
                    </option>
                  ))
                ) : (
                  districts.map((district) => (
                    <option key={district.value} value={district.value}>
                      {district.label}
                    </option>
                  ))
                )}
              </select>
            </div>
          </div>

          {/* Local Body Selection (only show for certain types) */}
          {['muncipality', 'blockpanchayat', 'gramapanchayat'].includes(localBodyType) && (
            <div className={`select-wrapper select-wrapper-2 ${styles.selectWrapper2}`}>
              <select
                name="localBodies"
                className="form-select form-select-2"
                value={selectedLocalBody}
                onChange={(e) => setSelectedLocalBody(e.target.value)}
                disabled={localBodies.length === 0}
              >
                {localBodies.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
            </div>
          )}
        </>
      )}

      {list && list !== 'state' && (
        <div className={`select-wrapper select-wrapper-2 ${styles.selectWrapper2}`}>
          <select
            name="districts"
            className="form-select"
            value={list && list !== 'state' ? list.toLowerCase() : selectedContent.toLowerCase()}
            onChange={(e) => {
              const selectedValue = e.target.value;
              handleContentSelect(selectedValue);
            }}
            disabled={!districts.length}
          >
            {districts.map((item) => (
              <option
                key={item.value}
                value={item.value.toLowerCase()}
              >
                {item.label}
              </option>
            ))}
          </select>
        </div>
      )}

      <div id={styles.partyWise}>
        {isLoading ? (
          <LoadingSpinner />
        ) : electionData ? (
          <div className={styles.partyResultsContainer}>
            {['LDF', 'UDF', 'NDA', 'Others'].map((front) => (
              electionData[front] && (
                <div key={front} className={styles.partyFrontTable}>
                  <table className={styles.partyTable}>
                    <thead>
                    <tr>
                      <th style={{textAlign: 'center'}}><span className={front.toLowerCase()}>{front}</span></th>
                      <th><span className={front.toLowerCase()}>{electionData[front].reduce((sum, party) => sum + parseInt(party.seat || 0), 0)}</span></th>
                      <th><span className={front.toLowerCase()}>{electionData[front].reduce((sum, party) => sum + parseInt(party.seat2020 || 0), 0)}</span></th>
                    </tr>
                    <tr>
                      <th>PARTY</th>
                      <th>2025</th>
                      <th>2020</th>
                      {/*<th>+/-</th>*/}
                    </tr>
                    </thead>
                    <tbody>
                    {electionData[front].map((party, index) => (
                      <tr key={index}>
                        <td>{party.party}</td>
                        <td>{party.seat || '0'}</td>
                        <td>{party.seat2020 || '0'}</td>
                        {/*<td className={parseInt(party.seat || 0) > parseInt(party.seat2015 || 0) ? 'gain' : 'loss'}>
                          {parseInt(party.seat || 0) - parseInt(party.seat2020 || 0) || ''}
                        </td>*/}
                      </tr>
                    ))}
                    </tbody>
                  </table>
                </div>
              )
            ))}
          </div>
        ) : (
          <LoadingSpinner />
        )}
      </div>
      {shareState?.showModal && (
        <ShareModal
          isOpen={shareState.showModal}
          onClose={() => setShareState(null)}
          shareLinks={shareState.shareLinks}
          shareText={shareState.shareText}
          url={shareState.url}
        />
      )}
    </div>
  );
};

export default PartyWiseResults;